import styled from "styled-components";

import { Text } from "@chakra-ui/react";
import { T } from "src/apps/components/i18n/Translation";
import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import { useIdentityVerification } from "../../../hooks/identityVerification/useIdentityVerification";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { IdentityVerificationDocumentTypes } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentTypes";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { Select } from "src/apps/components/select/Select";
import { IdentityVerificationDocumentTypeInformation } from "src/contexts/identityVerification/application/IdentityVerificationDocumentTypeInformation";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { TextBold } from "src/apps/components/typography/Texts";
import { colors } from "@permisso/permisso-components";
import { TEST_IDS } from "src/apps/config/testIds";

interface IdentityVerificationDocumentTypeSelectorProps {
  onSelectScrollBehaviour: () => void;
}

export const IdentityVerificationDocumentTypeSelector = ({
  onSelectScrollBehaviour,
}: IdentityVerificationDocumentTypeSelectorProps) => {
  const { allowedPairs, selectedDocumentType, selectDocumentType } = useIdentityVerification();
  const { identityVerifications } = useDocumentationArea();
  const { isMobile } = useIsMedia();
  const { t } = useTranslations();

  const docTypes = allowedPairs.filter((elem) => elem.countries.length > 0);
  const documentTypes = docTypes.map((elem) => elem.documentType);

  const onDocumentTypeSelected = (documentType: IdentityVerificationDocumentTypes) => {
    if (!documentType || documentType === selectedDocumentType) return;

    const match = documentTypes.find((type) => type === documentType);
    if (!match) {
      throw new Error("Document type value not found");
    }

    selectDocumentType(match);
    onSelectScrollBehaviour();
  };

  const isCompleted = identityVerifications[0].hasValidResponses();

  if (isCompleted) return null;

  return (
    <Spacer>
      {isMobile ? (
        <TextBold>
          <T k="documentation_area.identity_verification.select_document_type.title" />
        </TextBold>
      ) : (
        <Text>
          <T k="documentation_area.identity_verification.select_document_type.title" />
        </Text>
      )}
      <DocumentTypeSelectorContainer>
        <Select
          dataTestId={TEST_IDS.identityVerification.documentTypeSelect}
          placeholder={t("common.dropdown.placeholder")}
          options={
            documentTypes.map((documentType) => {
              return {
                key: t(IdentityVerificationDocumentTypeInformation[documentType].label),
                value: documentType,
              };
            }) ?? []
          }
          setSelectedValue={(documentType) =>
            onDocumentTypeSelected(documentType as IdentityVerificationDocumentTypes)
          }
          color={colors.grey800}
          value={selectedDocumentType ?? ""}
        />
      </DocumentTypeSelectorContainer>
    </Spacer>
  );
};

const Spacer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
`;

const DocumentTypeSelectorContainer = styled.div`
  display: grid;
  gap: 1rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    display: flex;
    flex-direction: column;
  }
`;
